import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { paymentsApi, FINANCIAL_TYPES } from "@/api";
import { useLang, fmtMoney, fmtDate } from "@/i18n";
import { StatusBadge, TypeBadge, ContactTypeBadge } from "@/components/Badges";
import { ContactDialog } from "@/components/ContactDialog";

const SideCard = ({ title, data, labels, testId }) => (
  <div className="erp-card p-5" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{title} · {data.count}</div>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 font-mono text-sm">
      <div><div className="font-body text-xs text-slate-500">{labels[0]}</div><div>{fmtMoney(data.total)}</div></div>
      <div><div className="font-body text-xs text-slate-500">{labels[1]}</div><div className="text-emerald-700 dark:text-emerald-300">{fmtMoney(data.paid)}</div></div>
      <div><div className="font-body text-xs text-slate-500">{labels[2]}</div><div className="text-xl font-medium" data-testid={`${testId}-balance`}>{fmtMoney(data.balance)}</div></div>
      <div><div className="font-body text-xs text-slate-500">{labels[3]}</div><div className={`text-xl font-medium ${data.overdue > 0 ? "text-red-600 dark:text-red-400" : "text-slate-400"}`} data-testid={`${testId}-overdue`}>{fmtMoney(data.overdue)}</div></div>
    </div>
  </div>
);

export default function ContactCard() {
  const { t, lang } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => paymentsApi.statement(id).then(setData).catch(() => toast.error(t("error")));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-slate-500 text-sm">…</div>;
  const { contact: c, documents, payments, receivable, payable } = data;
  const showRecv = c.type !== "supplier";
  const showPay = c.type !== "customer";

  return (
    <div className="space-y-6 page-enter" data-testid="contact-card-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/contacts")} data-testid="contact-card-back-button"><ArrowLeft size={18} /></Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="contact-card-name">{c.name}</h1>
              <ContactTypeBadge type={c.type} />
            </div>
            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300 flex flex-wrap gap-x-5 gap-y-1">
              <span className="font-mono">{t("vat_id")}: {c.afm || "—"} · {t("tax_office")}: {c.doy || "—"}</span>
              {(c.address || c.city) && <span className="inline-flex items-center gap-1"><MapPin size={13} />{c.address}{c.city ? `, ${c.city}` : ""}</span>}
              {c.phone && <span className="inline-flex items-center gap-1 font-mono"><Phone size={13} />{c.phone}</span>}
              {c.email && <span className="inline-flex items-center gap-1"><Mail size={13} />{c.email}</span>}
              <span>{t("payment_terms")}: <span className="font-mono">{c.payment_terms}</span></span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="contact-card-edit-button"><Pencil size={14} className="mr-1" />{t("edit")}</Button>
          <Button size="sm" className="bg-blue-800 hover:bg-blue-700" onClick={() => navigate("/documents/new")} data-testid="contact-card-new-doc-button"><Plus size={14} className="mr-1" />{t("new_document")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 stagger">
        {showRecv && <SideCard title={t("receivable")} data={receivable} labels={[t("invoiced"), t("collected"), t("open_balance"), t("overdue_balance")]} testId="receivable-card" />}
        {showPay && <SideCard title={t("payable")} data={payable} labels={[t("invoiced"), t("paid_out"), t("open_balance"), t("overdue_balance")]} testId="payable-card" />}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="erp-card xl:col-span-2 overflow-hidden" data-testid="contact-documents-card">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800"><h3 className="font-heading font-semibold">{t("transactions")}</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm erp-table">
              <thead>
                <tr className="text-left">
                  <th className="px-4 py-2">{t("number")}</th>
                  <th className="px-4 py-2">{t("type")}</th>
                  <th className="px-4 py-2 hidden md:table-cell">{t("date")}</th>
                  <th className="px-4 py-2">{t("status")}</th>
                  <th className="px-4 py-2 text-right">{t("total")}</th>
                  <th className="px-4 py-2 text-right">{t("balance")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {documents.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`contact-doc-row-${d.id}`}>
                    <td className="px-4 py-2.5"><Link to={`/documents/${d.id}`} className="font-mono font-medium text-blue-800 dark:text-blue-300 hover:underline">{d.number}</Link></td>
                    <td className="px-4 py-2.5"><TypeBadge type={d.type} /></td>
                    <td className="px-4 py-2.5 hidden md:table-cell font-mono text-slate-500">{fmtDate(d.issue_date, lang)}</td>
                    <td className="px-4 py-2.5"><StatusBadge doc={d} /></td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtMoney(d.total)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${d.balance > 0 && FINANCIAL_TYPES.includes(d.type) ? "text-amber-700 dark:text-amber-300" : "text-slate-400"}`}>
                      {FINANCIAL_TYPES.includes(d.type) ? fmtMoney(d.balance) : "—"}
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">{t("no_results")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="erp-card overflow-hidden" data-testid="contact-payments-card">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800"><h3 className="font-heading font-semibold">{t("payments_history")}</h3></div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {payments.map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`contact-payment-${p.id}`}>
                <div>
                  <Link to={`/documents/${p.document_id}`} className="font-mono text-blue-800 dark:text-blue-300 hover:underline">{p.document_number}</Link>
                  <div className="text-xs text-slate-500">{fmtDate(p.date, lang)} · {t(`pay_${p.method}`)}{p.reference ? ` · ${p.reference}` : ""}</div>
                </div>
                <span className={`font-mono font-medium ${p.direction === "in" ? "text-emerald-700 dark:text-emerald-300" : "text-orange-700 dark:text-orange-300"}`}>
                  {p.direction === "in" ? "+" : "−"} {fmtMoney(p.amount)}
                </span>
              </div>
            ))}
            {payments.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">{t("no_payments")}</div>}
          </div>
        </div>
      </div>

      <ContactDialog open={editOpen} onOpenChange={setEditOpen} contact={c} onSaved={load} />
    </div>
  );
}
